import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function StoreOperations({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: '📊' },
    { id: 'daily-sales', name: 'Daily Sales', icon: '💰' },
    { id: 'grn', name: 'GRN & Stock', icon: '📦' },
    { id: 'reconciliation', name: 'Reconciliation', icon: '🧾' },
    { id: 'reports', name: 'Reports', icon: '📈' }
  ];

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedStore && activeTab === 'dashboard') {
      fetchDashboardStats();
    }
  }, [selectedStore, activeTab]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data.stores || []);
      if (response.data.stores && response.data.stores.length > 0) {
        setSelectedStore(response.data.stores[0]);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/store-operations/dashboard?store_id=${selectedStore.store_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6">
          <div className="text-center py-12">Loading stores...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Store Operations</h1>
            <p className="mt-2 text-gray-600">Manage daily store operations, sales, and inventory</p>
          </div>
          
          {/* Store Selector */}
          <div className="w-64">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Store</label>
            <select
              value={selectedStore?.store_id || ''}
              onChange={(e) => {
                const store = stores.find(s => s.store_id === e.target.value);
                setSelectedStore(store);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              {stores.map(store => (
                <option key={store.store_id} value={store.store_id}>
                  {store.store_name} ({store.store_code})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-[#2b3380] text-[#2b3380]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'dashboard' && selectedStore && (
            <DashboardTab store={selectedStore} stats={dashboardStats} onNavigate={handleTabChange} />
          )}
          {activeTab === 'daily-sales' && selectedStore && (
            <DailySalesTab store={selectedStore} />
          )}
          {activeTab === 'grn' && selectedStore && (
            <GRNTab store={selectedStore} />
          )}
          {activeTab === 'reconciliation' && selectedStore && (
            <ReconciliationTab store={selectedStore} />
          )}
          {activeTab === 'reports' && selectedStore && (
            <ReportsTab store={selectedStore} />
          )}
        </div>
      </div>
    </Layout>
  );
}

// ==================== DASHBOARD TAB ====================
function DashboardTab({ store, stats, onNavigate }) {
  if (!stats) {
    return <div className="text-center py-12">Loading dashboard...</div>;
  }

  return (
    <div>
      {/* Store Info Card */}
      <div className="bg-gradient-to-r from-[#2b3380] to-[#1f2660] rounded-lg shadow-lg p-6 text-white mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">{store.store_name}</h2>
            <p className="mt-1 opacity-90">{store.store_code} • {store.city}</p>
          </div>
          <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
            <div className="text-sm opacity-90">Status</div>
            <div className="text-lg font-bold">{store.status}</div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-2">Today's Sales</div>
          <div className="text-3xl font-bold text-green-600">₹{stats.today_sales?.toLocaleString() || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Net sales for today</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-2">Month Sales</div>
          <div className="text-3xl font-bold text-[#2b3380]">₹{stats.month_sales?.toLocaleString() || 0}</div>
          <div className="text-xs text-gray-500 mt-1">This month total</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-2">Pending GRNs</div>
          <div className="text-3xl font-bold text-orange-600">{stats.pending_grns || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Awaiting receipt</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600 mb-2">Pending Recos</div>
          <div className="text-3xl font-bold text-purple-600">{stats.pending_reconciliations || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Needs approval</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-4 gap-4">
          <button onClick={() => onNavigate('daily-sales')} className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#2b3380] hover:bg-[#2b338015] transition">
            <div className="text-3xl mb-2">💰</div>
            <div className="font-medium">Record Sales</div>
          </button>
          <button onClick={() => onNavigate('grn')} className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#2b3380] hover:bg-[#2b338015] transition">
            <div className="text-3xl mb-2">📦</div>
            <div className="font-medium">Create GRN</div>
          </button>
          <button onClick={() => onNavigate('reconciliation')} className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#2b3380] hover:bg-[#2b338015] transition">
            <div className="text-3xl mb-2">🧾</div>
            <div className="font-medium">Daily Reco</div>
          </button>
          <button onClick={() => onNavigate('reports')} className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#2b3380] hover:bg-[#2b338015] transition">
            <div className="text-3xl mb-2">📈</div>
            <div className="font-medium">View Reports</div>
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== DAILY SALES TAB ====================
function DailySalesTab({ store }) {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchSales();
  }, [store]);

  const fetchSales = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/daily-sales?store_id=${store.store_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSales(response.data.sales || []);
    } catch (error) {
      console.error('Error fetching sales:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading sales data...</div>;
  }

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-bold">Daily Sales Records</h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
        >
          + Add Sales
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gross Sales</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discounts</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Sales</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cash</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Digital</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Reconciled</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sales.map((sale) => (
              <tr key={sale.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium">{new Date(sale.sale_date).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-sm text-right">₹{sale.gross_sales?.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-right text-red-600">-₹{sale.discounts?.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-right font-semibold">₹{sale.net_sales?.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-right">₹{sale.cash_sales?.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-right">₹{(sale.card_sales + sale.upi_sales + sale.wallet_sales)?.toLocaleString()}</td>
                <td className="px-6 py-4 text-center">
                  <span className={`px-2 py-1 text-xs rounded-full ${sale.is_reconciled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {sale.is_reconciled ? 'Yes' : 'No'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sales.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No sales records found. Add your first record!
          </div>
        )}
      </div>

      {showAddModal && (
        <AddSalesModal
          store={store}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchSales();
          }}
        />
      )}
    </div>
  );
}

// ==================== ADD SALES MODAL ====================
function AddSalesModal({ store, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    sale_date: new Date().toISOString().split('T')[0],
    gross_sales: 0,
    discounts: 0,
    cash_sales: 0,
    card_sales: 0,
    upi_sales: 0,
    wallet_sales: 0,
    returns_amount: 0,
    store_expenses: 0
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/daily-sales`, {
        store_id: store.store_id,
        ...formData
      }, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      onSuccess();
    } catch (error) {
      console.error('Error saving sales:', error);
      alert(error.response?.data?.detail || 'Error saving sales');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="p-6 border-b">
          <h3 className="text-xl font-bold">Add Daily Sales</h3>
          <p className="text-sm text-gray-600">{store.store_name}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date *</label>
              <input
                type="date"
                value={formData.sale_date}
                onChange={(e) => setFormData({...formData, sale_date: e.target.value})}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Gross Sales *</label>
              <input
                type="number"
                value={formData.gross_sales}
                onChange={(e) => setFormData({...formData, gross_sales: parseFloat(e.target.value) || 0})}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Discounts</label>
              <input
                type="number"
                value={formData.discounts}
                onChange={(e) => setFormData({...formData, discounts: parseFloat(e.target.value) || 0})}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Cash Sales</label>
              <input
                type="number"
                value={formData.cash_sales}
                onChange={(e) => setFormData({...formData, cash_sales: parseFloat(e.target.value) || 0})}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Card Sales</label>
              <input
                type="number"
                value={formData.card_sales}
                onChange={(e) => setFormData({...formData, card_sales: parseFloat(e.target.value) || 0})}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">UPI Sales</label>
              <input
                type="number"
                value={formData.upi_sales}
                onChange={(e) => setFormData({...formData, upi_sales: parseFloat(e.target.value) || 0})}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Sales'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== GRN TAB ====================
function GRNTab({ store }) {
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchGRNs();
  }, [store]);

  const fetchGRNs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/grn?store_id=${store.store_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrns(response.data.grns || []);
    } catch (error) {
      console.error('Error fetching GRNs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Received': 'bg-[#2b338030] text-[#2b3380]',
      'Verified': 'bg-green-100 text-green-800',
      'Discrepancy': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-12">Loading GRNs...</div>;
  }

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-bold">Goods Receipt Notes</h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
        >
          + Create GRN
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">GRN #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Items</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {grns.map((grn) => (
              <tr key={grn.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-[#2b3380]">{grn.grn_number}</td>
                <td className="px-6 py-4 text-sm">{new Date(grn.grn_date).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-sm">{grn.from_warehouse || grn.supplier || 'N/A'}</td>
                <td className="px-6 py-4 text-sm text-right">{grn.total_items}</td>
                <td className="px-6 py-4 text-sm text-right">{grn.total_quantity}</td>
                <td className="px-6 py-4 text-sm text-right">₹{grn.total_value?.toLocaleString()}</td>
                <td className="px-6 py-4 text-center">{getStatusBadge(grn.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {grns.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No GRNs found. Create your first GRN!
          </div>
        )}
      </div>

      {showAddModal && (
        <AddGRNModal
          store={store}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchGRNs();
          }}
        />
      )}
    </div>
  );
}

// ==================== ADD GRN MODAL ====================
function AddGRNModal({ store, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    grn_date: new Date().toISOString().split('T')[0],
    from_warehouse: 'Central Warehouse',
    po_number: '',
    items: [{ product_name: '', ordered_qty: 0, price: 0 }]
  });
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_name: '', ordered_qty: 0, price: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'product_name' ? value : parseFloat(value) || 0;
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/grn`, {
        store_id: store.store_id,
        ...formData
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating GRN:', error);
      alert(error.response?.data?.detail || 'Error creating GRN');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h3 className="text-xl font-bold">Create GRN</h3>
          <p className="text-sm text-gray-600">{store.store_name}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">GRN Date *</label>
              <input
                type="date"
                value={formData.grn_date}
                onChange={(e) => setFormData({...formData, grn_date: e.target.value})}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">From Warehouse *</label>
              <input
                type="text"
                value={formData.from_warehouse}
                onChange={(e) => setFormData({...formData, from_warehouse: e.target.value})}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">PO Number</label>
              <input
                type="text"
                value={formData.po_number}
                onChange={(e) => setFormData({...formData, po_number: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium">Items *</label>
              <button
                type="button"
                onClick={addItem}
                className="text-sm text-[#2b3380] hover:text-[#2b3380]"
              >
                + Add Item
              </button>
            </div>
            <div className="space-y-2">
              {formData.items.map((item, index) => (
                <div key={index} className="grid grid-cols-4 gap-2">
                  <input
                    type="text"
                    placeholder="Product name"
                    value={item.product_name}
                    onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                    required
                    className="col-span-2 border rounded px-3 py-2 text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Quantity"
                    value={item.ordered_qty}
                    onChange={(e) => updateItem(index, 'ordered_qty', e.target.value)}
                    required
                    className="border rounded px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Price"
                      value={item.price}
                      onChange={(e) => updateItem(index, 'price', e.target.value)}
                      required
                      className="flex-1 border rounded px-3 py-2 text-sm"
                    />
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create GRN'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== RECONCILIATION TAB ====================
function ReconciliationTab({ store }) {
  const [recos, setRecos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchRecos();
  }, [store]);

  const fetchRecos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/reconciliation?store_id=${store.store_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecos(response.data.reconciliations || []);
    } catch (error) {
      console.error('Error fetching reconciliations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (recoId, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/reconciliation/${recoId}/approve?action=${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRecos();
    } catch (error) {
      console.error('Error processing reconciliation:', error);
      alert('Error processing reconciliation');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Matched': 'bg-[#2b338030] text-[#2b3380]',
      'Variance': 'bg-orange-100 text-orange-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-12">Loading reconciliations...</div>;
  }

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-bold">Daily Reconciliation</h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660]"
        >
          + Create Reco
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reco #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sales</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Expected Cash</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actual Cash</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Variance</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {recos.map((reco) => (
              <tr key={reco.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-[#2b3380]">{reco.reco_number}</td>
                <td className="px-6 py-4 text-sm">{new Date(reco.reco_date).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-sm text-right">₹{reco.total_sales?.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-right">₹{reco.expected_closing_cash?.toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-right">₹{reco.actual_closing_cash?.toLocaleString()}</td>
                <td className={`px-6 py-4 text-sm text-right font-semibold ${reco.cash_variance > 0 ? 'text-green-600' : reco.cash_variance < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  ₹{reco.cash_variance?.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-center">{getStatusBadge(reco.status)}</td>
                <td className="px-6 py-4 text-center">
                  {reco.status === 'Pending' || reco.status === 'Variance' ? (
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleApprove(reco.id, 'approve')}
                        className="text-green-600 hover:text-green-800 text-sm"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleApprove(reco.id, 'reject')}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Reject
                      </button>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {recos.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No reconciliations found. Create your first reco!
          </div>
        )}
      </div>

      {showAddModal && (
        <AddRecoModal
          store={store}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchRecos();
          }}
        />
      )}
    </div>
  );
}

// ==================== ADD RECO MODAL ====================
function AddRecoModal({ store, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    reco_date: new Date().toISOString().split('T')[0],
    pos_sales: 0,
    opening_cash: 0,
    cash_sales: 0,
    expenses: 0,
    actual_closing_cash: 0,
    card_sales: 0,
    upi_sales: 0
  });
  const [saving, setSaving] = useState(false);

  const expectedClosing = formData.opening_cash + formData.cash_sales - formData.expenses;
  const variance = formData.actual_closing_cash - expectedClosing;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/reconciliation`, {
        store_id: store.store_id,
        ...formData
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating reconciliation:', error);
      alert(error.response?.data?.detail || 'Error creating reconciliation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl">
        <div className="p-6 border-b">
          <h3 className="text-xl font-bold">Daily Reconciliation</h3>
          <p className="text-sm text-gray-600">{store.store_name}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date *</label>
              <input
                type="date"
                value={formData.reco_date}
                onChange={(e) => setFormData({...formData, reco_date: e.target.value})}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">POS Sales *</label>
              <input
                type="number"
                value={formData.pos_sales}
                onChange={(e) => setFormData({...formData, pos_sales: parseFloat(e.target.value) || 0})}
                required
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-sm">Cash Reconciliation</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Opening Cash</label>
                <input
                  type="number"
                  value={formData.opening_cash}
                  onChange={(e) => setFormData({...formData, opening_cash: parseFloat(e.target.value) || 0})}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Cash Sales</label>
                <input
                  type="number"
                  value={formData.cash_sales}
                  onChange={(e) => setFormData({...formData, cash_sales: parseFloat(e.target.value) || 0})}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Expenses</label>
                <input
                  type="number"
                  value={formData.expenses}
                  onChange={(e) => setFormData({...formData, expenses: parseFloat(e.target.value) || 0})}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Actual Closing Cash *</label>
                <input
                  type="number"
                  value={formData.actual_closing_cash}
                  onChange={(e) => setFormData({...formData, actual_closing_cash: parseFloat(e.target.value) || 0})}
                  required
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="pt-2 border-t">
              <div className="flex justify-between text-sm">
                <span>Expected Closing:</span>
                <span className="font-semibold">₹{expectedClosing.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm font-bold mt-1">
                <span>Variance:</span>
                <span className={variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-gray-600'}>
                  ₹{variance.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Card Sales</label>
              <input
                type="number"
                value={formData.card_sales}
                onChange={(e) => setFormData({...formData, card_sales: parseFloat(e.target.value) || 0})}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">UPI Sales</label>
              <input
                type="number"
                value={formData.upi_sales}
                onChange={(e) => setFormData({...formData, upi_sales: parseFloat(e.target.value) || 0})}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Submit Reconciliation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== REPORTS TAB ====================
function ReportsTab({ store }) {
  const [reportType, setReportType] = useState('sales');
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (reportType === 'sales') {
        const response = await axios.get(
          `${API}/api/daily-sales?store_id=${store.store_id}&from_date=${dateRange.from}&to_date=${dateRange.to}`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        const sales = response.data.sales || [];
        
        const totalSales = sales.reduce((sum, s) => sum + (s.net_sales || 0), 0);
        const totalCash = sales.reduce((sum, s) => sum + (s.cash_sales || 0), 0);
        const totalDigital = sales.reduce((sum, s) => sum + (s.card_sales + s.upi_sales + s.wallet_sales || 0), 0);
        
        setReportData({
          type: 'Sales Report',
          records: sales.length,
          totalSales,
          totalCash,
          totalDigital,
          avgDailySales: sales.length > 0 ? totalSales / sales.length : 0
        });
      }
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    if (!reportData) return;
    const rows = [
      ['Store', store.store_name], ['Report', reportData.type], ['From', dateRange.from], ['To', dateRange.to],
      ['Records', reportData.records], ['Total Sales', reportData.totalSales], ['Total Cash', reportData.totalCash],
      ['Total Digital', reportData.totalDigital], ['Average Daily Sales', reportData.avgDailySales]
    ];
    const csv = rows.map(row => row.map(value => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${store.store_code || 'store'}-${reportType}-report.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Report Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4">Generate Report</h3>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="sales">Sales Report</option>
              <option value="variance">Variance Report</option>
              <option value="grn">GRN Report</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">From Date</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">To Date</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={generateReport}
              disabled={loading}
              className="w-full px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">{reportData.type}</h3>
            <button onClick={exportReport} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              Export to Excel
            </button>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Sales</div>
              <div className="text-2xl font-bold">₹{reportData.totalSales?.toLocaleString()}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Cash</div>
              <div className="text-2xl font-bold">₹{reportData.totalCash?.toLocaleString()}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Digital</div>
              <div className="text-2xl font-bold">₹{reportData.totalDigital?.toLocaleString()}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Avg Daily Sales</div>
              <div className="text-2xl font-bold">₹{Math.round(reportData.avgDailySales).toLocaleString()}</div>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            {reportData.records} records found for the selected period
          </div>
        </div>
      )}

      {!reportData && !loading && (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          Select report parameters and click Generate to view results
        </div>
      )}
    </div>
  );
}

export default StoreOperations;
